import { createContext, useEffect, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { auth, db } from "./firebase/config";

export const AuthContext = createContext();

export const GUEST_USER = {
  uid: "guest-demo-user",
  email: "recruiter@example.com",
  displayName: "Demo Recruiter",
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isGuest, setIsGuest] = useState(false);

  useEffect(() => {
    // Check if guest mode is stored in localStorage
    const storedGuest = localStorage.getItem("guestMode") === "true";
    if (storedGuest) {
      setUser(GUEST_USER);
      setRole("teacher");
      setIsAdmin(false);
      setIsGuest(true);
      setLoading(false);
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      const loadUserProfile = async () => {
        setUser(firebaseUser);

        if (!firebaseUser) {
          setRole(null);
          setIsAdmin(false);
          setIsGuest(false);
          setLoading(false);
          return;
        }

        try {
          const userRef = doc(db, "users", firebaseUser.uid);
          const snap = await getDoc(userRef);

          if (!snap.exists()) {
            const newProfile = {
              uid: firebaseUser.uid,
              email: firebaseUser.email || "",
              displayName: firebaseUser.displayName || "",
              role: "teacher",
              createdAt: serverTimestamp(),
            };

            await setDoc(userRef, newProfile, { merge: true });
            setRole("teacher");
            setIsAdmin(false);
          } else {
            const data = snap.data();
            const resolvedRole = data?.role || "teacher";
            setRole(resolvedRole);
            setIsAdmin(resolvedRole === "admin");
          }
        } catch (err) {
          console.error("Failed to load user profile", err);
          setRole("teacher");
          setIsAdmin(false);
        } finally {
          setLoading(false);
        }
      };

      loadUserProfile();
    });

    return () => unsubscribe();
  }, []);

  const loginAsGuest = () => {
    localStorage.setItem("guestMode", "true");
    setUser(GUEST_USER);
    setRole("teacher");
    setIsAdmin(false);
    setIsGuest(true);
  };

  const logoutGuest = async () => {
    localStorage.removeItem("guestMode");
    setUser(null);
    setRole(null);
    setIsAdmin(false);
    setIsGuest(false);
  };

  return (
    <AuthContext.Provider value={{ user, role, isAdmin, loading, isGuest, loginAsGuest, logoutGuest }}>
      {children}
    </AuthContext.Provider>
  );
}